import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Shield, 
  Heart, 
  ArrowRight, 
  Check, 
  MapPin, 
  Phone
} from "lucide-react";

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
    },
  };

  return (
    <section className="relative w-full min-h-[calc(100vh-96px)] overflow-hidden flex items-center justify-center bg-[#020506] py-12 lg:py-20">
      {/* Background Video (Subtle Ambient Texture) */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-12 scale-[1.02]"
        style={{ minWidth: "100%", minHeight: "100%" }}
      >
        <source src="/janak-hero.mp4" type="video/mp4" />
      </video>

      {/* Subtle Grid Background Pattern */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.12) 1px, transparent 0)`,
          backgroundSize: "28px 28px"
        }}
      />

      {/* Cinematic Darkening Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#020506]/90 via-[#020506]/60 to-[#020506] pointer-events-none" />
      
      {/* High-End Colorful Ambient Mesh Glows */}
      <div className="absolute left-1/4 top-1/4 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-[#3AAFA9]/12 blur-[120px] pointer-events-none animate-pulse" style={{ animationDuration: '8s' }} />
      <div className="absolute right-1/4 bottom-1/4 translate-x-1/2 translate-y-1/2 h-[600px] w-[600px] rounded-full bg-[#F4A896]/8 blur-[140px] pointer-events-none animate-pulse" style={{ animationDuration: '12s' }} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[800px] w-[800px] rounded-full bg-[#85E3DE]/6 blur-[160px] pointer-events-none" />

      {/* Bottom fade line */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#020506] to-transparent pointer-events-none" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* LEFT COLUMN: High Impact Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="lg:col-span-7 xl:col-span-6 flex flex-col text-left items-start"
          >
            {/* Top Pill Badge */}
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 rounded-full border border-[#3AAFA9]/30 bg-[#3AAFA9]/5 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#85E3DE] backdrop-blur-md mb-6"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
              </span>
              JOINT ASSURANCE NETWORK FOR AGING KIN
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl font-black leading-[1.12] text-white tracking-tight"
            >
              Care beyond distance for the{" "}
              <span className="text-[#3AAFA9]">people you</span>{" "}
              <span className="text-[#F4A896]">love.</span>
            </motion.h1>

            {/* Description Subtext */}
            <motion.p
              variants={itemVariants}
              className="mt-6 max-w-xl text-base leading-relaxed text-[#D2D9DB]/80 sm:text-lg font-normal"
            >
              JANAK connects you to your parents through verified home visits, real-time health updates, and instant emergency response. Care beyond distance, made simple.
            </motion.p>

            {/* Premium CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="mt-8 flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
            >
              <Link
                to="/services"
                className="group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-full bg-[#3AAFA9] px-8 py-4 text-sm font-bold text-white shadow-[0_8px_24px_rgba(58,175,169,0.3)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(58,175,169,0.45)] active:scale-[0.98]"
              >
                Explore Services
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full border border-white/20 bg-transparent px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/5 active:scale-[0.98]"
              >
                Talk to Team
              </Link>
            </motion.div>

            {/* Credibility Trust Metrics */}
            <motion.div
              variants={itemVariants}
              className="mt-10 pt-8 border-t border-white/5 w-full max-w-xl grid grid-cols-2 sm:grid-cols-4 gap-6 text-left"
            >
              <div>
                <div className="text-2xl sm:text-3xl font-black text-white tracking-tight">24/7</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1 font-bold">Support</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center text-emerald-400 shrink-0">
                    <Check size={11} strokeWidth={3} />
                  </span>
                  Verified
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1 font-bold">Janak Partners</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-2">
                  <MapPin size={18} className="text-[#85E3DE] shrink-0" />
                  Live
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1 font-bold">Care Updates</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-2">
                  <Heart size={18} className="text-[#F4A896] shrink-0" />
                  SOS
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1 font-bold">Emergency Alert</div>
              </div>
            </motion.div>
          </motion.div>

          {/* RIGHT COLUMN: Realistic Interactive Mobile Screen Mockup */}
          <div className="lg:col-span-5 xl:col-span-6 flex justify-center lg:justify-end items-center relative py-6">
            
            {/* Visual Backlight Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] bg-[#3AAFA9]/20 rounded-full blur-[110px] pointer-events-none" />
            
            {/* Main Phone Mockup Frame */}
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-[310px] sm:w-[340px] h-[660px] sm:h-[685px] rounded-[46px] border-[8px] border-[#1e293b] bg-[#090C15] shadow-[0_25px_60px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.12)] overflow-hidden flex flex-col z-20 select-none text-left"
            >
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-5 bg-[#1e293b] rounded-b-2xl z-50 flex items-center justify-center">
                <div className="w-10 h-1 bg-white/20 rounded-full mb-0.5" />
              </div>

              {/* Status Bar */}
              <div className="pt-6 px-5 pb-1 flex justify-between items-center text-[10px] font-semibold text-gray-400 bg-transparent">
                <span>10:42 AM</span>
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#10b981]/10 border border-[#10b981]/25">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-emerald-400 font-bold text-[8px] uppercase tracking-wider">Live Secure</span>
                </div>
              </div>

              {/* Internal Screen Body */}
              <div className="flex-1 px-4 py-3.5 flex flex-col gap-3.5 overflow-y-auto bg-[#090C15]">
                
                {/* Card 1: Dad's Safety Shield */}
                <div className="bg-[#121622] border border-white/5 rounded-2xl p-3 flex items-center justify-between shadow-md">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8.5 h-8.5 rounded-full bg-[#3AAFA9]/15 border border-[#3AAFA9]/25 flex items-center justify-center text-[#3AAFA9] font-black text-sm">
                      J
                    </div>
                    <div>
                      <div className="text-[11px] font-extrabold text-white leading-tight">Dad's Safety Shield</div>
                      <div className="text-[8px] text-[#A3B3B6] mt-0.5 font-medium">Guardian Assigned</div>
                    </div>
                  </div>
                  <div className="px-2.5 py-0.5 rounded-full border border-white/10 text-[7px] font-bold text-gray-400 tracking-wider">
                    FAMILY PORTAL
                  </div>
                </div>

                {/* Card 2: Live Visit Progress */}
                <div className="bg-[#121622] border border-white/5 rounded-2xl p-3 shadow-md space-y-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-[7.5px] font-black tracking-wider text-gray-400 uppercase">LIVE VISIT PROGRESS</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#3AAFA9]/15 text-[#3AAFA9] border border-[#3AAFA9]/25 text-[7px] font-bold">En Route</span>
                  </div>
                  
                  {/* Curved Dotted Progress Map */}
                  <div className="relative w-full h-11 flex items-center justify-center">
                    <svg className="w-full h-full" viewBox="0 0 260 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path 
                        d="M15 32 C 60 12, 110 40, 150 22 C 185 8, 215 30, 245 15" 
                        stroke="#3AAFA9" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeDasharray="3 4"
                      />
                      <circle cx="15" cy="32" r="3" fill="#3AAFA9" />
                      {/* Pulse ring */}
                      <circle cx="120" cy="27" r="9" fill="#3AAFA9" fillOpacity="0.15" />
                      <circle cx="120" cy="27" r="6" fill="#3AAFA9" stroke="#0B0F19" strokeWidth="1.2" />
                      <circle cx="120" cy="27" r="2" fill="#FFFFFF" />
                      
                      <circle cx="245" cy="15" r="3" fill="#F4A896" />
                    </svg>
                  </div>

                  {/* Caregiver Info Row */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#3AAFA9]/25 border border-[#3AAFA9]/35 flex items-center justify-center text-white text-[9px] font-black">
                        VS
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-white leading-tight">Vikranth Singh</div>
                        <div className="text-[7.5px] text-gray-400 font-semibold">Senior Care Specialist</div>
                      </div>
                    </div>
                    <div className="text-[7.5px] font-extrabold text-[#85E3DE] bg-[#85E3DE]/10 px-2 py-0.5 rounded-full">
                      5 min away
                    </div>
                  </div>
                </div>

                {/* Card 3: BP and SpO2 Vitals */}
                <div className="grid grid-cols-2 gap-2.5">
                  {/* BP Card */}
                  <div className="bg-[#121622] border border-white/5 rounded-2xl p-2.5 shadow-md">
                    <span className="text-[6.5px] font-black text-gray-400 uppercase tracking-wider block">BLOOD PRESSURE</span>
                    <div className="mt-1 flex items-baseline gap-0.5">
                      <span className="text-sm font-black text-white">118/76</span>
                      <span className="text-[7px] text-[#A3B3B6] font-bold uppercase">mmHg</span>
                    </div>
                    <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[6.5px] font-bold">
                      <span className="w-1 h-1 rounded-full bg-emerald-400" /> Optimal
                    </span>
                  </div>

                  {/* O2 Card */}
                  <div className="bg-[#121622] border border-white/5 rounded-2xl p-2.5 shadow-md">
                    <span className="text-[6.5px] font-black text-gray-400 uppercase tracking-wider block">OXYGEN LEVEL</span>
                    <div className="mt-1 flex items-baseline gap-0.5">
                      <span className="text-sm font-black text-white">99%</span>
                      <span className="text-[7px] text-[#A3B3B6] font-bold uppercase">SpO2</span>
                    </div>
                    <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[6.5px] font-bold">
                      <span className="w-1 h-1 rounded-full bg-emerald-400" /> Optimal
                    </span>
                  </div>
                </div>

                {/* Card 4: Today's Visits Logs */}
                <div className="bg-[#121622] border border-white/5 rounded-2xl p-3 shadow-md space-y-2">
                  <span className="text-[7.5px] font-black tracking-wider text-gray-400 uppercase">TODAY'S VISITS LOGS</span>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                        <Check size={8} strokeWidth={3} />
                      </div>
                      <div className="flex-1 flex justify-between text-[8px]">
                        <span className="font-semibold text-gray-400">10:30 AM</span>
                        <span className="font-bold text-white">Medication Administered</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                        <Check size={8} strokeWidth={3} />
                      </div>
                      <div className="flex-1 flex justify-between text-[8px]">
                        <span className="font-semibold text-gray-400">10:45 AM</span>
                        <span className="font-bold text-white">Vitals Recorded & Checked</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 opacity-40">
                      <div className="w-3.5 h-3.5 rounded-full border border-gray-600 flex items-center justify-center text-gray-400" />
                      <div className="flex-1 flex justify-between text-[8px]">
                        <span className="font-semibold text-gray-400">11:15 AM</span>
                        <span className="font-bold text-white">Companion Garden Walk</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 5: SOS Trigger Card */}
                <div className="bg-rose-950/20 border border-rose-500/20 rounded-2xl p-2.5 flex items-center justify-between shadow-md relative overflow-hidden mt-auto">
                  {/* Glow */}
                  <div className="absolute right-0 top-0 w-12 h-12 bg-rose-500/10 rounded-full blur-xl pointer-events-none" />
                  <div>
                    <div className="text-[9.5px] font-black text-rose-400 uppercase tracking-wide">Instant SOS Trigger</div>
                    <div className="text-[7px] text-rose-300/70 mt-0.5 font-medium">Dispatches Emergency services...</div>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-rose-500 border border-rose-400 flex items-center justify-center text-white shadow-[0_2px_8px_rgba(239,68,68,0.4)] animate-pulse">
                    <Phone size={10} className="fill-white" />
                  </div>
                </div>

              </div>

              {/* Bottom Home Indicator */}
              <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full" />

            </motion.div>

            {/* FLOATING 3D CARDS AROUND MOBILE MOCKUP */}
            {/* Top-Left Floating Badge: Heart Rate */}
            <motion.div
              initial={{ x: -25, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="absolute -left-8 top-32 z-30 hidden xl:flex flex-col gap-1.5 bg-[#0B0F19]/95 border border-white/10 rounded-2xl p-3 shadow-[0_12px_36px_rgba(0,0,0,0.5)] backdrop-blur-xl max-w-[140px] text-left"
            >
              <div className="flex items-center justify-between gap-6">
                <span className="text-[8px] text-white/50 font-bold uppercase tracking-wider">HEART RATE</span>
                <Heart size={12} className="fill-rose-500 text-rose-500 animate-pulse" />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-base font-black text-white">78</span>
                <span className="text-[8px] text-white/50 font-bold">BPM</span>
              </div>
              
              {/* Pulse line SVG */}
              <svg className="w-full h-5" viewBox="0 0 100 20" fill="none">
                <path d="M0 10 H30 L35 2 L40 18 L45 8 L50 12 L55 10 H100" stroke="#EF4444" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
              
              <span className="inline-flex items-center gap-1 text-[7px] text-emerald-400 font-bold">
                <span className="w-1 h-1 rounded-full bg-emerald-400" /> Vitals Normal
              </span>
            </motion.div>

            {/* Bottom-Right Floating Badge: Emergency */}
            <motion.div
              initial={{ x: 25, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="absolute -right-8 top-72 z-30 hidden xl:flex items-center gap-2.5 bg-[#0B0F19]/95 border border-white/10 rounded-2xl p-3 shadow-[0_12px_36px_rgba(0,0,0,0.5)] backdrop-blur-xl text-left"
            >
              <div className="w-8 h-8 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                <Shield size={16} />
              </div>
              <div>
                <span className="text-[7.5px] text-white/50 font-bold uppercase tracking-wider block">EMERGENCY</span>
                <span className="text-[11px] font-black text-white block">Dispatcher Active</span>
                <span className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[6.5px] font-bold">
                  15 min avg response
                </span>
              </div>
            </motion.div>

          </div>

        </div>
      </div>

      {/* Floating Scroll Down Mouse Icon */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 cursor-pointer opacity-30 hover:opacity-75 transition-opacity duration-300 pointer-events-none hidden lg:flex">
        <span className="text-[9px] uppercase tracking-[0.25em] text-white/50 font-semibold">Scroll</span>
        <div className="w-5 h-8 rounded-full border border-white/20 flex justify-center p-1.5">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="w-1 h-1 rounded-full bg-[#3AAFA9]"
          />
        </div>
      </div>
    </section>
  );
};

export default Hero;
